import React, { useMemo } from 'react';
import { useIntl, type MessageDescriptor } from 'react-intl';
import clsx from 'clsx';
import type { FootageFailCode } from 'fym-shared/src/music24/musicVideoApi';
import { TypedMemo } from '../../../common/types';
import type { FootageView } from '../../footage/footageView';
import { useMusicVideoEditor } from '../../MusicVideoEditorContext';
import { footageMessages } from '../../messages/footageMessages';
import { ClipCard } from './ClipCard';
import { useAddClip } from './useAddClip';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the editor; each part uses its own slice of it */
import styles from '../../MusicVideoEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

const FAIL_MESSAGES: Readonly<Record<FootageFailCode, MessageDescriptor>> = {
  REFUSED: footageMessages.aiRefused,
  QUALITY_CHECK: footageMessages.aiQualityFailed,
  BUDGET_EXCEEDED: footageMessages.aiBudgetFailed,
  GENERATION_FAILED: footageMessages.aiFailed,
  STALLED: footageMessages.aiFailed,
  PROVIDER_QUOTA: footageMessages.aiProviderQuotaFailed,
};

/** The newest AI clips the list shows. */
const LIST_COUNT = 8;

/** One AI clip: working, ready (it drags), refused or failed (design h2). */
const AiClipRow: React.FC<{ view: FootageView; credits: number; onPick: (view: FootageView) => void }> = ({
  view,
  credits,
  onPick,
}) => {
  const intl = useIntl();
  const isWorking = view.status === 'queued' || view.status === 'generating';
  const isFailed = view.status === 'failed';
  const label = view.prompt ?? view.label;
  return (
    <div className={clsx(styles.aiRow, isWorking && styles.aiRowWorking, isFailed && styles.aiRowBad)}>
      <ClipCard view={view} showSource onPick={onPick} />
      <div>
        <b>{view.label}</b>
        <small title={label}>
          {isWorking
            ? intl.formatMessage(footageMessages.aiMaking)
            : isFailed
              ? intl.formatMessage(FAIL_MESSAGES[view.failCode ?? 'GENERATION_FAILED'], { credits })
              : intl.formatMessage(footageMessages.aiReady)}
        </small>
      </div>
    </div>
  );
};

/** "Your AI clips" of this song, newest first (design h1, h2). */
const AiClipListComponent: React.FC = () => {
  const intl = useIntl();
  const { footage, catalogItemUuid, data } = useMusicVideoEditor();
  const { add } = useAddClip();
  const clips = useMemo(
    () =>
      footage.own
        .filter((view) => view.origin === 'ai' && view.catalogItemUuid === catalogItemUuid)
        .sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''))
        .slice(0, LIST_COUNT),
    [catalogItemUuid, footage.own]
  );
  const hasWorking = useMemo(
    () => clips.some((view) => view.status === 'queued' || view.status === 'generating'),
    [clips]
  );
  if (clips.length === 0) {
    return null;
  }
  return (
    <>
      <div className={styles.sectionHead}>
        <b>{intl.formatMessage(footageMessages.aiYours)}</b>
        <small>{intl.formatMessage(footageMessages.aiYoursFacts, { count: clips.length })}</small>
      </div>
      {hasWorking && <p className={styles.alertInfo}>{intl.formatMessage(footageMessages.aiWorkingNote)}</p>}
      <div className={styles.column}>
        {clips.map((view) => (
          <AiClipRow key={view.key} view={view} credits={data.plan.aiClipCredits} onPick={add} />
        ))}
      </div>
      <p className={styles.hint}>{intl.formatMessage(footageMessages.aiLimitHint)}</p>
    </>
  );
};

export const AiClipList = TypedMemo(AiClipListComponent);
